// ListingCardList.tsx
import { useState } from "react";
import { motion } from "framer-motion";
import {
  Home,
  Building2,
  Clock,
  Calendar,
  MapPin,
  Star,
  Heart,
  Share2,
  Wind,
  Tv,
  Wifi,
  Coffee,
  Car,
  Users,
  Briefcase,
  Eye,
  ChevronRight,
  Shield,
  Droplet,
  Dumbbell,
  Waves,
  Sofa,
  Dog,
  Zap,

  BookOpen,
  Umbrella,
  Leaf,
  ArrowUp,
  DoorOpen,
} from "lucide-react";

// Updated TransformedListing interface matching your schema
interface TransformedListing {
  id: string;
  product_id: string;
  title: string;
  price: number;
  priceUnit: "month" | "night" | "hour" | "day" | "total";
  location: string;
  address_line1: string;
  timeAgo: string;
  type: "residential" | "commercial" | "short-stay";
  bedrooms?: number;
  bathrooms?: number;
  area?: number;
  amenities: string[];
  status: "available" | "rented" | "maintenance" | "coming_soon" | "sold";
  latitude: number;
  longitude: number;
  images?: string[];
  rating?: number;
  reviews?: number;
  isNew: boolean;
  isFeatured: boolean;
  viewingCount: number;
  favoriteCount: number;
  furnished?: "unfurnished" | "semi-furnished" | "fully-furnished";
  parkingSpots: number;
  petFriendly: boolean;
  immediateAvailable: boolean;
  utilitiesIncluded: boolean;
  minimumLeaseMonths?: number;
  maximumLeaseMonths?: number;
  depositAmount?: number;
  maintenanceFee?: number;
}

interface ListingCardListProps {
  listing: TransformedListing;
  onViewDetails?: (listing: TransformedListing) => void;
  onLike?: (listing: TransformedListing) => void;
}

// Complete amenity icons mapping
const amenityIcons: Record<string, any> = {
  // Cooling
  "Air Conditioning": Wind,
  AC: Wind,
  
  // Internet
  WiFi: Wifi,
  Wifi: Wifi,
  
  // Entertainment
  TV: Tv,
  
  // Parking
  Parking: Car,
  
  // Kitchen
  Kitchen: Home,
  "Kitchen Access": Home,
  
  // Laundry
  "Washing Machine": Home,
  Dryer: Home,
  
  // Fitness
  Gym: Dumbbell,
  
  // Recreation
  Pool: Waves,
  
  // Bathroom
  "Hot Water": Droplet,
  
  // Accessibility
  Elevator: ArrowUp,
  "Wheelchair Access": Users,
  
  // Security
  Security: Shield,
  
  // Outdoor
  Balcony: Home,
  Garden: Leaf,
  "Beach Access": Umbrella,
  
  // Furniture
  Furnished: Sofa,
  "Study Desk": BookOpen,
  
  // Pets
  "Pets Allowed": Dog,
  
  // Smoking
  "Smoking Allowed": Coffee,
  
  // Views
  "Ocean View": Eye,
  "City View": Eye,
  
  // Access
  "Private Entrance": DoorOpen,
  
  // Utility
  "Backup Generator": Zap,
  "Water Tank": Droplet,
  
  // Commercial
  Workspace: Briefcase,
  "Display Area": Eye,
  Storage: Home,
  "Water Heater": Droplet,
  Restaurant: Coffee,
  "Mini Fridge": Home,
  "Private Bathroom": Home,
  Spa: Users,
};

export default function ListingCardList({ listing, onViewDetails, onLike }: ListingCardListProps) {
  const [isLiked, setIsLiked] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const formatPrice = () => {
    const price = listing?.price?.toLocaleString();
    let unitText = "";
    switch (listing.priceUnit) {
      case "month":
        unitText = "/month";
        break;
      case "night":
        unitText = "/night";
        break;
      case "hour":
        unitText = "/hour";
        break;
      case "day":
        unitText = "/day";
        break;
      case "total":
        unitText = " total";
        break;
      default:
        unitText = "";
    }
    return `MVR ${price}${unitText}`;
  };

  const getStatusColor = () => {
    switch (listing.status) {
      case "available":
        return "bg-green-500";
      case "rented":
        return "bg-red-500";
      case "maintenance":
        return "bg-orange-500";
      case "coming_soon":
        return "bg-yellow-500";
      case "sold":
        return "bg-gray-500";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusText = () => {
    switch (listing.status) {
      case "available":
        return "Available";
      case "rented":
        return "Rented";
      case "maintenance":
        return "Under Maintenance";
      case "coming_soon":
        return "Coming Soon";
      case "sold":
        return "Sold";
      default:
        return listing.status;
    }
  };

  const getTypeIcon = () => {
    switch (listing.type) {
      case "residential":
        return <Home className="w-4 h-4" />;
      case "commercial":
        return <Building2 className="w-4 h-4" />;
      case "short-stay":
        return <Clock className="w-4 h-4" />;
      default:
        return <Home className="w-4 h-4" />;
    }
  };

  const getFurnishedText = () => {
    switch (listing.furnished) {
      case "fully-furnished":
        return "Fully Furnished";
      case "semi-furnished":
        return "Semi-Furnished";
      case "unfurnished":
        return "Unfurnished";
      default:
        return null;
    }
  };

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLiked(!isLiked);
    onLike?.(listing);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Implement share functionality
    if (navigator.share) {
      navigator.share({
        title: listing.title,
        text: `Check out this ${listing.type} property`,
        url: `${window.location.origin}/listings/${listing.product_id}`,
      });
    } else {
      navigator.clipboard.writeText(`${window.location.origin}/listings/${listing.product_id}`);
      alert("Link copied to clipboard!");
    }
  };

  const handleClick = () => {
    onViewDetails?.(listing);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={handleClick}
      className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all cursor-pointer"
    >
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Left Section - Image Thumbnail */}
          <div className="relative w-24 h-24 md:w-32 md:h-32 flex-shrink-0 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg overflow-hidden">
            {/* Badges */}
            <div className="absolute top-2 left-2 flex gap-1">
              {listing.isNew && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-purple-500 text-white rounded">
                  New
                </span>
              )}
              {listing.isFeatured && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-yellow-500 text-white rounded">
                  Featured
                </span>
              )}
              {listing.type === "short-stay" && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-500 text-white rounded">
                  Short Stay
                </span>
              )}
              {listing.immediateAvailable && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-green-500 text-white rounded">
                  Move-in Ready
                </span>
              )}
            </div>

            {/* Type Icon */}
            <div className="absolute bottom-2 right-2 p-1 bg-white/90 dark:bg-gray-800/90 rounded-md backdrop-blur-sm">
              {getTypeIcon()}
            </div>

            {/* Image placeholder - you can add actual image here */}
            {listing.image_url ? (
              <img 
                src={listing.image_url} 
                alt={listing.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {getTypeIcon()}
              </div>
            )}
          </div>
          
          {/* Middle Section - Content */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {listing.timeAgo}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                <span className="truncate max-w-[200px]">{listing.location}</span>
              </span>
              {listing.bedrooms !== undefined && (
                <span className="flex items-center gap-1">
                  <Home className="w-3 h-3" />
                  {listing.bedrooms} bed{listing.bedrooms !== 1 ? 's' : ''}
                </span>
              )}
              {listing.bathrooms !== undefined && (
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {listing.bathrooms} bath{listing.bathrooms !== 1 ? 's' : ''}
                </span>
              )}
              {listing.area !== undefined && (
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {listing.area} sqft
                </span>
              )}
            </div>
            
            <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white mb-2 line-clamp-1">
              {listing.title}
            </h3>
            
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <span className="text-lg md:text-xl font-bold text-purple-600 dark:text-purple-400">
                {formatPrice()}
              </span>
              
              {listing.rating && listing.rating > 0 && (
                <span className="flex items-center gap-1 text-sm text-yellow-500">
                  <Star className="w-4 h-4 fill-current" />
                  {listing.rating} ({listing.reviews || 0} reviews)
                </span>
              )}
              
              {listing.status === "available" && (
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
                  <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                    {getStatusText()}
                  </span>
                </div>
              )}

              {getFurnishedText() && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  • {getFurnishedText()}
                </span>
              )}

              {listing.petFriendly && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  • Pet Friendly
                </span>
              )}

              {listing.utilitiesIncluded && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  • Utilities Included
                </span>
              )}
            </div>
            
            {/* Amenities */}
            <div className="flex flex-wrap gap-2">
              {listing?.amenities?.slice(0, 4).map((amenity) => {
                const Icon = amenityIcons[amenity] || Home;
                return (
                  <span key={amenity} className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                    <Icon className="w-3 h-3" />
                    {amenity}
                  </span>
                );
              })}
              {listing?.amenities?.length > 4 && (
                <span className="text-xs text-gray-500">
                  +{listing.amenities.length - 4} more
                </span>
              )}
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
              {listing.viewingCount > 0 && (
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {listing.viewingCount} views
                </span>
              )}
              {listing.favoriteCount > 0 && (
                <span className="flex items-center gap-1">
                  <Heart className="w-3 h-3" />
                  {listing.favoriteCount} saved
                </span>
              )}
              {listing.parkingSpots > 0 && (
                <span className="flex items-center gap-1">
                  <Car className="w-3 h-3" />
                  {listing.parkingSpots} parking spot{listing.parkingSpots !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          
          {/* Right Section - Actions */}
          <div className="flex flex-col gap-2">
            <button
              onClick={handleLike}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={isLiked ? "Unlike" : "Like"}
            >
              <Heart 
                className={`w-5 h-5 transition-colors ${
                  isLiked 
                    ? "fill-red-500 text-red-500" 
                    : "text-gray-400 hover:text-red-500"
                }`} 
              />
            </button>
            <button
              onClick={handleShare}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Share"
            >
              <Share2 className="w-5 h-5 text-gray-400 hover:text-purple-500" />
            </button>
            <button
              onClick={handleClick}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors md:hidden"
              title="View details"
            >
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
        
        {/* Hover indicator for desktop */}
        <div className={`hidden md:block mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <button className="text-sm text-purple-500 hover:text-purple-600 font-medium flex items-center gap-1">
            View full details
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}